import { useQuery } from '@apollo/client';
import { type ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { InfoBox } from '@ics-portal/guardian';
import { GET_PROJECT_GUIDANCES_BY_PROJECT_TYPE } from '@api/queries/projects';
import { GET_ALL_STANDARDS_RICH } from '@api/queries/standards';
import { StandardProfiles } from '@components/StandardProfiles';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type ProjectFormAttributes } from '../../ProjectForm';

interface ProjectFormStandardsProps {
    isEditMode?: boolean;
}

export function ProjectFormStandards({
    isEditMode,
}: ProjectFormStandardsProps): ReactNode {
    const {
        watch,
        setValue,
        clearErrors,
        formState: { errors },
    } = useFormContext<ProjectFormAttributes>();
    const { pid } = useParams();

    const { data: standardsData, loading: standardsLoading } = useQuery(
        GET_ALL_STANDARDS_RICH,
    );

    const projectTypeId = watch('typeID');
    const { data: projectGuidancesData } = useQuery(
        GET_PROJECT_GUIDANCES_BY_PROJECT_TYPE,
        {
            skip: !projectTypeId,
            variables: { projectTypeId },
        },
    );

    const allowedProfileIds = useMemo(() => {
        if (!projectGuidancesData) return undefined;

        return flatMapEdges(projectGuidancesData.projectGuidances).map(
            (item) => item.standardProfile.id,
        );
    }, [projectGuidancesData]);

    const standards = useMemo(
        () => flatMapEdges(standardsData?.standards ?? { edges: [] }),
        [standardsData],
    );

    const standardProfileIDs = watch('standardProfileIDs');

    const handleProfileChange = (profileId: string, checked: boolean): void => {
        const current = standardProfileIDs;

        const containingStandard = standards.find((standard) =>
            (standard.standardProfiles ?? []).some((p) => p?.id === profileId),
        );

        if (!containingStandard) {
            setValue('standardProfileIDs', [], { shouldDirty: true });
            clearErrors('standardProfileIDs');
            return;
        }

        const siblingProfileIds = (containingStandard.standardProfiles ?? [])
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .map((p) => p.id);

        const withoutSiblings = current.filter(
            (existingId) => !siblingProfileIds.includes(existingId),
        );

        setValue(
            'standardProfileIDs',
            checked ? [...withoutSiblings, profileId] : withoutSiblings,
            { shouldDirty: true },
        );
        clearErrors('standardProfileIDs');
    };

    const handleStandardClear = (standardId: string): void => {
        const standardToExclude =
            standards
                .find((standard) => standard.id === standardId)
                ?.standardProfiles?.map(
                    (standardProfile) => standardProfile?.id,
                ) ?? [];
        const withoutSiblings = standardProfileIDs.filter(
            (existingId) => !standardToExclude.includes(existingId),
        );

        setValue('standardProfileIDs', withoutSiblings, { shouldDirty: true });
        clearErrors('standardProfileIDs');
    };

    const invalid = Boolean(errors.standardProfileIDs);
    const messageItems = isEditMode
        ? [
              'Standard profiles can not be changed. To change Standard profiles you need to start a new project.',
          ]
        : [
              'Standard profiles are optional.',
              'You can choose only one standard profile per standard.',
              'Standard profiles cannot be changed once the first task has started.',
          ];

    return (
        <div className="flex flex-column gap-3">
            <InfoBox items={messageItems} />
            <StandardProfiles
                standards={standards}
                loading={standardsLoading}
                selectedProfileIds={standardProfileIDs}
                onProfileChange={handleProfileChange}
                onStandardClear={handleStandardClear}
                allowedProfileIds={allowedProfileIds}
                projectId={pid}
                readonly={Boolean(isEditMode)}
                invalid={invalid}
                singleSelect
            />
        </div>
    );
}
